import { getCurrentScope, onScopeDispose, ref, type Ref } from 'vue';
import type { ProductSearch, ProductSearchProgress } from '@/entities/product';
import type { SearchProgressStage } from './types';
import { SERP_PROGRESS_FINISH_MS, SERP_PROGRESS_TICK_MS } from '../config/progress';
import { getDriftValue, getJobsFill, getSearchProgressStage, getStageEnd } from '../lib/searchProgress';

export interface SearchProgress {
  value: Ref<number>;
  stage: Ref<SearchProgressStage | null>;
  isVisible: Ref<boolean>;
}

export function useSearchProgress(productSearch: ProductSearch): SearchProgress {
  const isVisible = ref(true);
  const stage = ref<SearchProgressStage | null>('operators');
  const value = ref(0);

  let jobsFill = 0;
  let driftFrom = 0;
  let driftStartedAt = Date.now();
  let ticker: ReturnType<typeof setInterval> | null = null;
  let finishTimer: ReturnType<typeof setTimeout> | null = null;

  function startTicking(): void {
    if (ticker) return;

    ticker = setInterval(drift, SERP_PROGRESS_TICK_MS);
  }

  function stopTicking(): void {
    if (!ticker) return;

    clearInterval(ticker);
    ticker = null;
  }

  function cancelFinish(): void {
    if (!finishTimer) return;

    clearTimeout(finishTimer);
    finishTimer = null;
  }

  function drift(): void {
    value.value = Math.max(value.value, getDriftValue(driftFrom, getStageEnd(jobsFill), Date.now() - driftStartedAt));
  }

  function update(progress: ProductSearchProgress): void {
    const nextJobsFill = getJobsFill(progress);

    stage.value = getSearchProgressStage(Math.max(jobsFill, nextJobsFill));

    if (nextJobsFill <= jobsFill) return;

    jobsFill = nextJobsFill;
    value.value = Math.max(value.value, jobsFill);
    driftFrom = value.value;
    driftStartedAt = Date.now();
  }

  function restart(progress: ProductSearchProgress, startedAt: number): void {
    jobsFill = 0;
    driftFrom = 0;
    driftStartedAt = startedAt;
    value.value = 0;
    update(progress);
    drift();
    startTicking();
  }

  const unsubscribes = [
    productSearch.on('start', () => {
      cancelFinish();
      isVisible.value = true;
      restart(productSearch.progress, Date.now());
    }),
    productSearch.on('progress', update),
    productSearch.on('end', () => {
      stopTicking();
      value.value = 1;
      cancelFinish();
      finishTimer = setTimeout(() => {
        finishTimer = null;
        stage.value = null;
        isVisible.value = false;
      }, SERP_PROGRESS_FINISH_MS);
    }),
  ];

  if (getCurrentScope()) {
    onScopeDispose(() => {
      stopTicking();
      cancelFinish();
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    });
  }

  if (isVisible.value) restart(productSearch.progress, productSearch.timings.startedAt ?? Date.now());

  return { value, stage, isVisible };
}
